<template>
  <div
    class="medhop-assistant"
    :class="{ 'medhop-assistant--open': open, 'medhop-assistant--dragging': dragging }"
    :style="positionStyle"
  >
    <button
      class="medhop-assistant__trigger"
      type="button"
      aria-label="Buka Aime, AI Assistant Medhop"
      :aria-expanded="open ? 'true' : 'false'"
      @pointerdown="startDrag"
      @click="toggleAssistant"
    >
      <span class="medhop-assistant__trigger-avatar" aria-hidden="true">
        <i v-if="open" class="fas fa-times"></i>
        <img v-else src="/images/aime-avatar.png" alt="" draggable="false" />
        <small v-if="!open"><i class="fas fa-star"></i></small>
      </span>
      <span class="medhop-assistant__trigger-copy">
        <strong>Aime</strong>
        <small>Asisten Operasional</small>
      </span>
    </button>

    <transition name="assistant-panel">
      <section v-if="open" class="medhop-assistant__panel" aria-label="Aime, AI Assistant Medhop">
        <header class="medhop-assistant__header">
          <div class="medhop-assistant__avatar"><img src="/images/aime-avatar.png" alt="Avatar Aime" /></div>
          <div>
            <strong>Aime</strong>
            <span>{{ knowledgeStatus }}</span>
          </div>
          <button type="button" aria-label="Tutup assistant" @click="open = false">
            <i class="fas fa-times"></i>
          </button>
        </header>

        <div ref="messages" class="medhop-assistant__messages">
          <article v-for="(message, index) in messages" :key="index" :class="`is-${message.role}`">
            <div class="medhop-assistant__bubble">
              <p>{{ message.text }}</p>
              <div v-if="message.sources && message.sources.length" class="medhop-assistant__sources">
                <small>Sumber</small>
                <a
                  v-for="source in message.sources"
                  :key="`${source.kind}-${source.id}`"
                  :href="source.url || null"
                  :target="source.url ? '_blank' : null"
                  :rel="source.url ? 'noopener noreferrer' : null"
                  :class="{ 'is-static': !source.url }"
                >
                  <i :class="source.url ? 'fas fa-book-open' : 'fas fa-database'"></i>
                  {{ source.label }}
                </a>
              </div>
              <div v-if="message.actions && message.actions.length" class="medhop-assistant__message-actions">
                <button v-for="action in message.actions" :key="action.route" type="button" @click="runAction(action)">
                  Buka {{ action.label }} <i class="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </article>
          <article v-if="loading" class="is-assistant is-loading">
            <div class="medhop-assistant__bubble"><p><i class="fas fa-circle-notch fa-spin"></i> Memeriksa panduan dan akses Anda…</p></div>
          </article>
        </div>

        <div class="medhop-assistant__context">
          <small>Saran di halaman ini</small>
          <div>
            <button
              v-for="action in contextualActions"
              :key="action.label"
              type="button"
              @click="runAction(action)"
            >
              <i class="fas fa-arrow-right"></i>{{ action.label }}
            </button>
          </div>
        </div>

        <form class="medhop-assistant__composer" @submit.prevent="sendMessage">
          <input
            v-model.trim="question"
            type="text"
            maxlength="240"
            autocomplete="off"
            placeholder="Tanya alur kerja atau cari menu…"
            aria-label="Pertanyaan untuk Medhop Assistant"
          />
          <button type="submit" :disabled="!question || loading" aria-label="Kirim pertanyaan">
            <i class="fas fa-arrow-up"></i>
          </button>
        </form>

        <p class="medhop-assistant__notice">
          Berbasis User Guide & akses Anda · bukan untuk keputusan klinis.
        </p>
      </section>
    </transition>
  </div>
</template>

<script>
import AimeService from "@/core/services/aime.service.js";

export default {
  name: "MedhopAssistant",
  data() {
    return {
      open: false,
      question: "",
      messages: [
        {
          role: "assistant",
          text: "Halo, saya Aime. Saya bantu mencari menu, menjelaskan User Guide Medhop, dan membaca ringkasan operasional sesuai hak akses Anda.",
        },
      ],
      assistantState: { guide: null, context: null },
      initialized: false,
      loading: false,
      initializationError: false,
      position: null,
      dragging: false,
      movedWhileDragging: false,
      dragOrigin: null,
    };
  },
  mounted() {
    this.restorePosition();
    this.initializeAime();
    window.addEventListener("resize", this.keepInViewport);
    window.addEventListener("medhop:product-configuration", this.refreshAimeContext);
  },
  beforeDestroy() {
    this.stopDrag();
    window.removeEventListener("resize", this.keepInViewport);
    window.removeEventListener("medhop:product-configuration", this.refreshAimeContext);
  },
  computed: {
    positionStyle() {
      if (!this.position) return null;
      return { left: `${this.position.x}px`, top: `${this.position.y}px`, right: "auto", bottom: "auto" };
    },
    knowledgeStatus() {
      if (this.loading && !this.initialized) return "Menyiapkan knowledge…";
      if (this.assistantState.guide) return `User Guide ${this.assistantState.guide.version || "aktif"} · Aman`;
      return "Panduan operasional · Mode terbatas";
    },
    contextualActions() {
      return AimeService.contextualMenus(this.$router, this.$route.path, this.assistantState.context);
    },
  },
  watch: {
    "$route.path"() {
      if (window.innerWidth < 768) this.open = false;
    },
  },
  methods: {
    toggleAssistant() {
      if (this.movedWhileDragging) {
        this.movedWhileDragging = false;
        return;
      }
      this.open = !this.open;
      if (this.open && !this.initialized && !this.loading) this.initializeAime();
    },
    startDrag(event) {
      if (event.button !== undefined && event.button !== 0) return;
      const rect = this.$el.getBoundingClientRect();
      this.dragging = true;
      this.movedWhileDragging = false;
      this.dragOrigin = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        elementX: rect.left,
        elementY: rect.top,
      };
      window.addEventListener("pointermove", this.onDrag);
      window.addEventListener("pointerup", this.stopDrag, { once: true });
    },
    onDrag(event) {
      if (!this.dragging || !this.dragOrigin) return;
      const dx = event.clientX - this.dragOrigin.pointerX;
      const dy = event.clientY - this.dragOrigin.pointerY;
      if (Math.abs(dx) + Math.abs(dy) > 5) this.movedWhileDragging = true;
      const rect = this.$el.getBoundingClientRect();
      const padding = 10;
      this.position = {
        x: Math.min(Math.max(padding, this.dragOrigin.elementX + dx), window.innerWidth - rect.width - padding),
        y: Math.min(Math.max(padding, this.dragOrigin.elementY + dy), window.innerHeight - rect.height - padding),
      };
    },
    stopDrag() {
      if (!this.dragging) return;
      this.dragging = false;
      this.dragOrigin = null;
      window.removeEventListener("pointermove", this.onDrag);
      if (this.position) localStorage.setItem("medhop_aime_position", JSON.stringify(this.position));
    },
    restorePosition() {
      try {
        const saved = JSON.parse(localStorage.getItem("medhop_aime_position"));
        if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) this.position = saved;
      } catch (_) {
        localStorage.removeItem("medhop_aime_position");
      }
      this.$nextTick(this.keepInViewport);
    },
    keepInViewport() {
      if (!this.position || !this.$el) return;
      const rect = this.$el.getBoundingClientRect();
      this.position = {
        x: Math.min(Math.max(10, this.position.x), Math.max(10, window.innerWidth - rect.width - 10)),
        y: Math.min(Math.max(10, this.position.y), Math.max(10, window.innerHeight - rect.height - 10)),
      };
    },
    async initializeAime() {
      if (this.loading) return;
      this.loading = true;
      try {
        this.assistantState = await AimeService.initialize();
        this.initialized = true;
        this.initializationError = !this.assistantState.guide;
      } catch (_) {
        this.initializationError = true;
      } finally {
        this.loading = false;
        this.scrollMessages();
      }
    },
    async refreshAimeContext() {
      this.initialized = false;
      await this.initializeAime();
    },
    runAction(action) {
      if (!action || !action.route) return;
      if (this.$route.fullPath !== action.route) this.$router.push(action.route).catch(() => {});
      if (window.innerWidth < 768) this.open = false;
    },
    scrollMessages() {
      this.$nextTick(() => {
        if (this.$refs.messages) this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;
      });
    },
    async sendMessage() {
      if (!this.question || this.loading) return;
      const question = this.question;
      this.messages.push({ role: "user", text: question });
      this.question = "";
      this.loading = true;
      this.scrollMessages();
      try {
        if (!this.initialized) {
          this.assistantState = await AimeService.initialize();
          this.initialized = true;
        }
        const answer = await AimeService.answer(question, this.$route.fullPath, this.$router, this.assistantState);
        this.messages.push({ role: "assistant", ...answer });
      } catch (_) {
        this.messages.push({
          role: "assistant",
          text: "Aime belum dapat memuat sumber bantuan. Silakan buka User Guide Medhop atau coba kembali.",
          sources: [{ id: "guide", label: "User Guide Medhop", url: "/guide/index.html", kind: "user_guide" }],
          actions: [],
        });
      } finally {
        this.loading = false;
      }
      if (this.messages.length > 30) this.messages = [this.messages[0]].concat(this.messages.slice(-29));
      this.scrollMessages();
    },
  },
};
</script>

<style scoped>
.medhop-assistant { position: fixed; right: 22px; bottom: 22px; z-index: 1090; user-select: none; touch-action: none; }
.medhop-assistant__trigger { display: inline-flex; align-items: center; gap: 10px; min-height: 58px; padding: 5px 15px 5px 6px; color: #fff !important; background: linear-gradient(135deg,var(--skin-deep),var(--skin-primary) 58%,var(--skin-secondary)) !important; border: 1px solid rgba(255,255,255,.24); border-radius: 999px; box-shadow: 0 14px 32px rgba(var(--skin-deep-rgb),.3); cursor: grab; }
.medhop-assistant__trigger * { color: inherit !important; }
.medhop-assistant__trigger-avatar { position: relative; display: grid; place-items: center; width: 46px; height: 46px; flex: 0 0 46px; color: var(--skin-primary) !important; background: linear-gradient(145deg,#fff,#dff7f2); border: 2px solid rgba(255,255,255,.82); border-radius: 50%; box-shadow: 0 4px 12px rgba(7,37,74,.22); font-size: 22px; overflow: visible; }
.medhop-assistant__trigger-avatar > i { color: var(--skin-primary) !important; }
.medhop-assistant__trigger-avatar > img { display: block; width: 100%; height: 100%; object-fit: cover; object-position: center 18%; border-radius: 50%; pointer-events: none; }
.medhop-assistant__trigger-avatar small { position: absolute; top: -3px; right: -2px; display: grid; place-items: center; width: 17px; height: 17px; color: #fff !important; background: #f4a340; border: 2px solid #fff; border-radius: 50%; font-size: 7px; }
.medhop-assistant__trigger-copy { display: flex; flex-direction: column; align-items: flex-start; min-width: 92px; line-height: 1.05; }
.medhop-assistant__trigger-copy strong { color: #fff !important; font-size: 15px; font-weight: 800; letter-spacing: .01em; }
.medhop-assistant__trigger-copy small { margin-top: 4px; color: rgba(255,255,255,.78) !important; font-size: 9px; font-weight: 650; letter-spacing: .04em; text-transform: uppercase; }
.medhop-assistant--dragging .medhop-assistant__trigger { cursor: grabbing; transform: scale(1.02); box-shadow: 0 18px 38px rgba(var(--skin-deep-rgb),.36); }
.medhop-assistant__panel { position: absolute; right: 0; bottom: 58px; display: flex; flex-direction: column; width: min(380px,calc(100vw - 28px)); max-height: min(620px,calc(100vh - 110px)); overflow: hidden; background: #fff; border: 1px solid #dfe7ef; border-radius: 18px; box-shadow: 0 24px 60px rgba(var(--skin-deep-rgb),.22); }
.medhop-assistant__header { display: grid; grid-template-columns: 42px 1fr 32px; align-items: center; gap: 10px; padding: 14px 15px; color: #fff; background: linear-gradient(135deg,var(--skin-deep),var(--skin-primary) 60%,var(--skin-secondary)); }
.medhop-assistant__avatar { display: grid; place-items: center; width: 38px; height: 38px; overflow: hidden; color: var(--skin-primary); background: #fff; border: 1px solid rgba(255,255,255,.5); border-radius: 50%; font-size: 18px; }
.medhop-assistant__avatar img { display: block; width: 100%; height: 100%; object-fit: cover; object-position: center 18%; }
.medhop-assistant__header strong,.medhop-assistant__header span { display: block; }.medhop-assistant__header span { margin-top: 2px; opacity: .78; font-size: 10px; }.medhop-assistant__header button { width: 32px; height: 32px; color: #fff; background: transparent; border: 0; border-radius: 9px; }
.medhop-assistant__messages { min-height: 180px; max-height: 340px; overflow-y: auto; padding: 15px; background: #f7fafc; }
.medhop-assistant__messages article { display: flex; margin-bottom: 9px; }.medhop-assistant__bubble { max-width: 92%; padding: 10px 12px; color: #334155; background: #fff; border: 1px solid #e2e8f0; border-radius: 13px 13px 13px 4px; font-size: 12px; line-height: 1.5; }.medhop-assistant__bubble p { margin: 0; white-space: pre-line; }.medhop-assistant__messages .is-user { justify-content: flex-end; }.medhop-assistant__messages .is-user .medhop-assistant__bubble { color: #fff; background: var(--skin-primary); border-color: var(--skin-primary); border-radius: 13px 13px 4px 13px; }.medhop-assistant__messages .is-loading .medhop-assistant__bubble { color: #64748b; }
.medhop-assistant__sources { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 9px; padding-top: 8px; border-top: 1px solid #edf2f7; }.medhop-assistant__sources small { flex-basis: 100%; color: #64748b; font-size: 9px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; }.medhop-assistant__sources a { display: inline-flex; align-items: center; gap: 4px; padding: 4px 7px; color: var(--skin-primary); background: var(--skin-soft); border: 1px solid var(--skin-line); border-radius: 7px; font-size: 9px; font-weight: 700; }.medhop-assistant__sources a.is-static { color: #64748b; pointer-events: none; }
.medhop-assistant__message-actions { display: flex; flex-direction: column; gap: 5px; margin-top: 8px; }.medhop-assistant__message-actions button { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 6px 8px; color: var(--skin-primary); background: #fff; border: 1px solid var(--skin-line); border-radius: 8px; font-size: 10px; font-weight: 700; text-align: left; }
.medhop-assistant__context { padding: 13px 15px 6px; }.medhop-assistant__context small { display: block; margin-bottom: 8px; color: #64748b; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; }.medhop-assistant__context div { display: flex; gap: 7px; overflow-x: auto; }.medhop-assistant__context button { flex: 0 0 auto; padding: 7px 10px; color: var(--skin-primary); background: var(--skin-soft); border: 1px solid var(--skin-line); border-radius: 9px; font-size: 11px; font-weight: 600; }.medhop-assistant__context i { margin-right: 5px; }
.medhop-assistant__composer { display: grid; grid-template-columns: 1fr 38px; gap: 8px; padding: 10px 15px 8px; }.medhop-assistant__composer input { min-width: 0; height: 40px; padding: 0 12px; border: 1px solid #dbe4ed; border-radius: 11px; outline: 0; font-size: 12px; }.medhop-assistant__composer input:focus { border-color: #5aa8a3; box-shadow: 0 0 0 3px rgba(var(--skin-secondary-rgb),.1); }.medhop-assistant__composer button { color: #fff; background: var(--skin-primary); border: 0; border-radius: 11px; }.medhop-assistant__composer button:disabled { opacity: .45; }
.medhop-assistant__notice { margin: 0; padding: 0 15px 12px; color: #94a3b8; font-size: 9px; text-align: center; }
.assistant-panel-enter-active,.assistant-panel-leave-active { transition: opacity .18s ease,transform .18s ease; }.assistant-panel-enter,.assistant-panel-leave-to { opacity: 0; transform: translateY(10px) scale(.98); }
@media (max-width: 575.98px) { .medhop-assistant { right: 12px; bottom: 12px; }.medhop-assistant__trigger { min-height: 52px; padding: 4px 12px 4px 5px; }.medhop-assistant__trigger-avatar { width: 42px; height: 42px; flex-basis: 42px; font-size: 20px; }.medhop-assistant__panel { position: fixed; right: 8px; bottom: 72px; left: 8px; width: auto; max-height: calc(100vh - 86px); border-radius: 16px; }.medhop-assistant__messages { min-height: 130px; } }
</style>
